import FallbackComponent from "@/components/FallbackComponent";
import ReportForm from "@/reports/components/ReportForm";
import { FORM_ACTION } from "@/reports/config/formActionType";
import useSaveReport from "@/reports/hooks/useSaveReport";
import { ReportType } from "@/reports/types/Api";
import { ReportId } from "@/reports/types/SafetyValveReport";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useLocation, useNavigate } from "react-router-dom";

interface Props {
  reportType?: ReportType;
}

const Create = ({ reportType = "SAFETY_VALVE" }: Props) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const location = state?.location;

  useEffect(() => {
    if (!location) {
      if (reportType === "MANUAL_VALVE") {
        navigate("/manual-valve-reports");
      } else {
        navigate("/safety-valve-reports");
      }
    }
  }, [location, navigate, reportType]);

  const [id, setId] = useState<ReportId>(() => ({
    value: nanoid(),
    onServer: false,
  }));

  const onCancel = useCallback(() => {
    if (reportType === "MANUAL_VALVE") {
      navigate("/manual-valve-reports");
    } else {
      navigate("/safety-valve-reports");
    }
  }, [navigate, reportType]);

  const {
    save: onlineSave,
    isSaved: isSavedOnline,
    setSaved: setSavedOnline,
  } = useSaveReport({
    id,
    setId,
    location,
    reportType,
  });

  return (
    <ReportForm
      onCancel={onCancel}
      save={onlineSave}
      title={
        reportType === "SAFETY_VALVE"
          ? "New Safety Valve Report"
          : "New Manual Valve Report"
      }
      isSaved={isSavedOnline}
      setSaved={setSavedOnline}
      formActionType={FORM_ACTION.CREATE}
      createFromCopy={state?.copyReportValues !== undefined}
      defaultValues={state?.copyReportValues || undefined}
      reportType={reportType}
    />
  );
};

export default withErrorBoundary(Create, {
  FallbackComponent,
});
